/**
 * HW Search Panel — custom element for Horizon Works search panel.
 *
 * States: closed → default → results → no-results
 *
 * Responsibilities:
 *  - Open/close panel + scroll lock
 *  - Header class toggle (hw-header--search-open)
 *  - Debounced AJAX predictive search
 *  - Client-side pill filtering
 *  - Results count formatting
 *  - Keyboard handling (Escape, CMD+K)
 */

class HwSearchPanel extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @type {AbortController | null} */
  #fetchController = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #debounceTimer = null;

  /** @type {number} */
  #activePillIndex = 0;

  /** @type {string | null} */
  #noResultsTemplate = null;

  /** @type {HTMLButtonElement | null} */
  #openTrigger = null;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Listen for open triggers (data-hw-search-open buttons in the header)
    document.addEventListener('click', this.#handleDocumentClick, { signal });

    // Close button inside panel
    this.querySelector('[data-hw-search-close]')?.addEventListener('click', () => this.close(), { signal });

    // Clear/reset button
    this.querySelector('[data-hw-search-clear]')?.addEventListener('click', () => this.#clearSearch(), { signal });

    // Backdrop click (rendered as sibling outside panel element)
    document.querySelector('[data-hw-search-backdrop]')?.addEventListener('click', () => this.close(), { signal });

    // Input: search on type
    const input = this.#input;
    if (input) {
      input.addEventListener('input', this.#handleInput, { signal });
      input.addEventListener('keydown', this.#handleInputKeydown, { signal });
    }

    // Pills
    this.querySelector('[data-hw-search-pills]')?.addEventListener('click', this.#handlePillClick, { signal });

    // Keyboard: Escape closes, CMD+K opens
    document.addEventListener('keydown', this.#handleGlobalKeydown, { signal });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#clearDebounce();
  }

  // ─── Getters ───────────────────────────────────────────

  get #input() {
    return /** @type {HTMLInputElement | null} */ (this.querySelector('[data-hw-search-input]'));
  }

  get #clearBtn() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hw-search-clear]'));
  }

  get #header() {
    return document.querySelector('#header-component');
  }

  get #sectionId() {
    return this.dataset.sectionId || 'hw-search-panel';
  }

  get #maxResults() {
    return parseInt(this.dataset.maxResults || '5', 10);
  }

  get #isOpen() {
    return !this.hasAttribute('hidden');
  }

  // ─── Open / Close ──────────────────────────────────────

  open(trigger = null) {
    if (this.#isOpen) return;

    this.#openTrigger = trigger;

    this.#syncOffsets();
    this.removeAttribute('hidden');
    this.#setFirstPillActive();
    this.#setState('default');

    this.#header?.classList.add('hw-header--search-open');
    document.body.style.overflow = 'hidden';
    document.querySelector('[data-hw-search-backdrop]')?.removeAttribute('hidden');

    requestAnimationFrame(() => {
      this.#input?.focus();
    });
  }

  close() {
    if (!this.#isOpen) return;

    this.setAttribute('hidden', '');
    this.dataset.state = '';

    this.#header?.classList.remove('hw-header--search-open');
    document.body.style.overflow = '';
    document.querySelector('[data-hw-search-backdrop]')?.setAttribute('hidden', '');
    this.#clearOffsets();

    this.#clearSearch();

    // Return focus to trigger
    if (this.#openTrigger) {
      this.#openTrigger.focus();
      this.#openTrigger = null;
    }
  }

  // ─── Panel offsets ─────────────────────────────────────

  /**
   * Anchor the panel to where the header actually sits right now.
   *
   * The CSS fallback (`--header-group-height` minus `--header-height`) is the
   * announcement bar's height, which only lines up while the bar is on screen.
   * The bar is not sticky, so once the page is scrolled the header is pinned to
   * the viewport top and that offset leaves a strip of live nav above the
   * panel. Scrolling is locked while the panel is open, so one measurement here
   * stays accurate until it closes.
   */
  #syncOffsets() {
    const header = this.#header;
    if (!(header instanceof HTMLElement)) return;

    const { top, height } = header.getBoundingClientRect();
    const panelTop = Math.max(0, top);

    document.body.style.setProperty('--hw-search-panel-top', `${panelTop}px`);
    document.body.style.setProperty('--hw-search-backdrop-top', `${panelTop + height}px`);
  }

  #clearOffsets() {
    document.body.style.removeProperty('--hw-search-panel-top');
    document.body.style.removeProperty('--hw-search-backdrop-top');
  }

  // ─── State ─────────────────────────────────────────────

  #setState(state) {
    this.dataset.state = state;
  }

  // ─── Event Handlers ────────────────────────────────────

  #handleDocumentClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const trigger = target.closest('[data-hw-search-open]');

    if (trigger) {
      event.preventDefault();
      if (this.#isOpen) {
        this.close();
      } else {
        this.open(/** @type {HTMLButtonElement} */ (trigger));
      }
      return;
    }

    // Click outside panel when open
    if (this.#isOpen && !this.contains(target)) {
      this.close();
    }
  };

  #handleGlobalKeydown = (event) => {
    if (event.key === 'Escape' && this.#isOpen) {
      event.preventDefault();
      this.close();
      return;
    }

    // CMD+K / CTRL+K — toggle panel
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      if (this.#isOpen) {
        this.close();
      } else {
        const trigger = document.querySelector('[data-hw-search-open]');
        this.open(/** @type {HTMLButtonElement | null} */ (trigger));
      }
    }
  };

  #handleInputKeydown = (event) => {
    if (event.key === 'Enter') {
      const value = this.#input?.value.trim();
      if (value) {
        const url = new URL(Theme.routes.search_url, location.origin);
        url.searchParams.set('q', value);
        url.searchParams.set('type', 'product');
        window.location.href = url.toString();
      }
    }
  };

  #handleInput = (event) => {
    const value = /** @type {HTMLInputElement} */ (event.target).value.trim();

    if (value.length > 0) {
      this.#showClear();
    } else {
      this.#hideClear();
      this.#clearDebounce();
      this.#fetchController?.abort();
      this.#setState('default');
      return;
    }

    this.#clearDebounce();
    this.#debounceTimer = setTimeout(() => this.#fetchResults(value), 300);
  };

  #handlePillClick = (event) => {
    const pill = /** @type {HTMLElement} */ (event.target).closest('[data-hw-search-pill]');
    if (!pill) return;

    const index = parseInt(/** @type {HTMLElement} */ (pill).dataset.pillIndex || '0', 10);

    // Update active state
    this.querySelectorAll('[data-hw-search-pill]').forEach((p) => {
      p.classList.remove('hw-search-panel__pill--active');
      p.setAttribute('aria-pressed', 'false');
    });
    pill.classList.add('hw-search-panel__pill--active');
    pill.setAttribute('aria-pressed', 'true');
    pill.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });

    this.#activePillIndex = index;
    this.#showPillGroup(index);
  };

  // ─── Search ────────────────────────────────────────────

  async #fetchResults(query) {
    this.#fetchController?.abort();
    this.#fetchController = new AbortController();

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('section_id', this.#sectionId);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', String(this.#maxResults));

    try {
      const response = await fetch(url.toString(), { signal: this.#fetchController.signal });
      if (!response.ok) throw new Error(`Search fetch failed: ${response.status}`);

      const html = await response.text();
      this.#injectResults(html, query);
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        console.error('[hw-search] Fetch error:', error);
      }
    }
  }

  #injectResults(html, query) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Extract products container from AJAX response
    const productsSource = doc.querySelector('[data-hw-search-products-results]');
    const countSource = doc.querySelector('[data-hw-search-count-value]');

    const productsTarget = this.querySelector('[data-hw-search-products-results]');
    if (productsTarget && productsSource) {
      productsTarget.innerHTML = productsSource.innerHTML;
    }

    const count = countSource ? parseInt(countSource.textContent?.trim() || '0', 10) : 0;

    if (count === 0) {
      this.#setState('no-results');
      this.#updateNoResultsText(query);
    } else {
      this.#setState('results');
      this.#updateResultsCount(count);
    }
  }

  // ─── Results count ─────────────────────────────────────

  #updateResultsCount(count) {
    const el = this.querySelector('[data-hw-search-results-count]');
    if (!el) return;

    const section = /** @type {HTMLElement} */ (document.querySelector('#shopify-section-' + this.#sectionId));
    let suffix = count === 1
      ? (section?.dataset.countSingular || 'Result')
      : (section?.dataset.countPlural || 'Results');

    // Read from data attributes set via section settings (passed via JS config)
    const panel = /** @type {HTMLElement} */ (this);
    if (panel.dataset.countSingular && count === 1) suffix = panel.dataset.countSingular;
    if (panel.dataset.countPlural && count !== 1) suffix = panel.dataset.countPlural;

    el.textContent = `${count} ${suffix}`;
  }

  #updateNoResultsText(query) {
    const el = this.querySelector('[data-hw-search-no-results-text]');
    if (!el) return;
    // Capture the pristine template (with %TERMS%) on first use — after the
    // first replace the placeholder is gone, so we must rebuild from the cache.
    if (this.#noResultsTemplate === null) {
      this.#noResultsTemplate = el.innerHTML;
    }
    el.innerHTML = this.#noResultsTemplate.replace('%TERMS%', this.#escapeHtml(query));
  }

  #escapeHtml(str) {
    return str.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] || c));
  }

  // ─── Pill group switching (default state) ─────────────

  #showPillGroup(index) {
    this.querySelectorAll('[data-pill-group]').forEach((group) => {
      const el = /** @type {HTMLElement} */ (group);
      const groupIndex = parseInt(el.dataset.pillGroup || '0', 10);
      if (groupIndex === index) {
        el.removeAttribute('hidden');
      } else {
        el.setAttribute('hidden', '');
      }
    });
  }

  #setFirstPillActive() {
    const pills = this.querySelectorAll('[data-hw-search-pill]');
    if (pills.length === 0) return;

    this.#activePillIndex = 0;
    this.#showPillGroup(0);
  }

  // ─── Helpers ───────────────────────────────────────────

  #clearSearch() {
    const input = this.#input;
    if (input) input.value = '';
    this.#hideClear();
    this.#clearDebounce();
    this.#fetchController?.abort();
    this.#setState('default');
    this.#showPillGroup(this.#activePillIndex);
  }

  #showClear() {
    const btn = this.#clearBtn;
    if (btn) btn.hidden = false;
  }

  #hideClear() {
    const btn = this.#clearBtn;
    if (btn) btn.hidden = true;
  }

  #clearDebounce() {
    if (this.#debounceTimer !== null) {
      clearTimeout(this.#debounceTimer);
      this.#debounceTimer = null;
    }
  }
}

if (!customElements.get('hw-search-panel')) {
  customElements.define('hw-search-panel', HwSearchPanel);
}
